/** @jsx jsx */
import React, { type ReactNode } from 'react';

import { css, styled, jsx } from '@compiled/react';
import type { PreloadedQuery } from 'react-relay';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import type { viewBoardCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBoardCustomFiltersQuery.graphql';
import { ImportWorkButton } from '@atlassian/jira-software-onboarding-invites';
import { useShowQuickFilters } from '@atlassian/jira-software-view-settings';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller';
import { isCmpUifFilterSelectionBarEnabled } from '../../../feature-flags';
import {
	useIsCMPBoard,
	useIsIncrementPlanningBoard,
	useIsJSMBoard,
	useIsJSWBoard,
} from '../../../state/state-hooks/capabilities';
import InsightsButton from '../insights-button';
import FilterBar from './filter-bar';
import FilterSelectionBar from './filter-selection-bar';
import { FullscreenButton } from './fullscreen-button';
import SwimlaneSwitch from './swimlane-switch';
import ViewSettings from './view-settings';

type Props = {
	isSprintsEnabled: boolean;
	hasActiveSprint: boolean;
	rapidViewId: number;
	customFiltersQueryRef: PreloadedQuery<viewBoardCustomFiltersQuery> | null | undefined;
	leftHandSideActions?: ReactNode;
	children?: ReactNode;
	showCardDetails?: boolean;
	boardType?: string;
};

const ControlsBar = ({
	rapidViewId,
	isSprintsEnabled,
	hasActiveSprint,
	customFiltersQueryRef,
	leftHandSideActions,
	showCardDetails = true,
	children,
}: Props) => {
	const appEditions = useAppEditions();
	const showInsightsButton = !isSprintsEnabled || hasActiveSprint;
	const isCMPBoard = useIsCMPBoard();
	const isJSMBoard = useIsJSMBoard();
	const isJSWBoard = useIsJSWBoard();
	const isIncrementPlanningBoard = useIsIncrementPlanningBoard();
	const isFilterBarVisible = useShowQuickFilters();

	const shouldRenderGroupBy = () => {
		/**
		 * Increment Planning board only has one swimlane grouping - Team (rendered by default).
		 * We don't allow users to switch to a different grouping therefore we're not rendering the SwimlaneSwitcher.
		 */
		if (isIncrementPlanningBoard) {
			return false;
		}

		/**
		 * Since this functionality is under experiment, we want to push forward this feature for JSM board.
		 * Remove this condition while cleaning `jira_software_cmp_user_based_swimlanes` as will not be necessary.
		 * After cleanup `jira_software_cmp_user_based_swimlanes`, please archilve `show-groupby-swimlane-option-for-jsm_ka7t1` or ask FF owner to archive the flag.
		 */
		if (ff('show-groupby-swimlane-option-for-jsm_ka7t1') && isJSMBoard) {
			return true;
		}

		// Render unconditionally after clean-up:
		return expValEquals('jira_software_cmp_user_based_swimlanes', 'cohort', 'experiment')
			? true
			: !isCMPBoard;
	};

	if (ff('sea-2744-jsw-tabs-nav') && isJSWBoard) {
		return (
			<Box xcss={outerBoxStylesNewStyles}>
				<Inline space="space.100" testId="software-board.header.controls-bar">
					<FilterBarWrapper>
						{/* @ts-expect-error - TS2741 - Property 'screenName' is missing in type '{ customFiltersQueryRef: PreloadedQuery<viewBoardCustomFiltersQuery, Record<string, unknown>> | null | undefined; }' */}
						<FilterBar customFiltersQueryRef={customFiltersQueryRef} />
					</FilterBarWrapper>
					{shouldRenderGroupBy() && <SwimlaneSwitch />}
					{leftHandSideActions}
					<Box xcss={importWorkWrapperStyles}>
						<ImportWorkButton />
					</Box>
					{showInsightsButton && (
						<InsightsButton
							boardId={rapidViewId}
							edition={appEditions.software}
							isSprintsEnabled={isSprintsEnabled}
						/>
					)}
					<FullscreenButton boardId={Number(rapidViewId)} />
					<ViewSettings showCardDetails={showCardDetails} />
					{children}
				</Inline>
				{isCmpUifFilterSelectionBarEnabled() && isFilterBarVisible && isCMPBoard && (
					<FilterSelectionBar />
				)}
			</Box>
		);
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives
		<div css={styles}>
			<Wrapper data-testid="software-board.header.controls-bar">
				<FilterBarWrapper>
					{/* @ts-expect-error - TS2741 - Property 'screenName' is missing in type '{ customFiltersQueryRef: PreloadedQuery<viewBoardCustomFiltersQuery, Record<string, unknown>> | null | undefined; }' */}
					<FilterBar customFiltersQueryRef={customFiltersQueryRef} />
				</FilterBarWrapper>

				{shouldRenderGroupBy() && <SwimlaneSwitch />}

				{leftHandSideActions}
				<Box xcss={importWorkWrapperStyles}>
					<ImportWorkButton />
				</Box>
				{showInsightsButton && (
					<InsightsWrapper>
						<InsightsButton
							boardId={rapidViewId}
							edition={appEditions.software}
							isSprintsEnabled={isSprintsEnabled}
						/>
					</InsightsWrapper>
				)}

				<ViewSettings showCardDetails={showCardDetails} />

				{children}
			</Wrapper>
			{isCmpUifFilterSelectionBarEnabled() && isFilterBarVisible && isCMPBoard && (
				<FilterSelectionBar />
			)}
		</div>
	);
};

export default ControlsBar;
const outerBoxStylesNewStyles = xcss({ marginBottom: 'space.200' });
const styles = css({ marginBottom: token('space.400', '32px') });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarWrapper = styled.div({
	flex: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InsightsWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '5px',
});

const importWorkWrapperStyles = xcss({
	marginLeft: 'space.050',
});
